import { Check, Circle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { QUESTION_TEMPLATES } from '@/features/schema-agent/intake'
import { AskSchemaAgent } from '@/features/schema-agent/SchemaConversationContext'
import type { SchemaProposal, SchemaQuestionCoverage } from '@/services/api/schemaSessions'
import { ReviewDisclosure } from './ReviewDisclosure'
import { EvidenceLinks } from './SchemaSources'

const FIXED_QUESTIONS = new Set<string>(QUESTION_TEMPLATES)

function TypeEvidence({ proposal }: { proposal: SchemaProposal }) {
  const [selected, setSelected] = useState<string | null>(null)
  const reason = selected ? proposal.typeReasons[selected] : undefined
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {Object.keys(proposal.typeReasons)
          .sort()
          .map(label => (
            <Button
              key={label}
              size="sm"
              variant={selected === label ? 'secondary' : 'outline'}
              aria-pressed={selected === label}
              onClick={() => setSelected(selected === label ? null : label)}
              className="h-8 text-xs"
            >
              {label}
            </Button>
          ))}
      </div>
      {reason && (
        <div className="space-y-3 border-l-2 border-primary pl-4">
          <h4 className="text-sm font-medium">{selected}</h4>
          <p className="text-sm leading-relaxed text-muted-foreground">{reason.why}</p>
          <EvidenceLinks evidence={reason.evidence} sources={proposal.sources ?? []} />
          {selected && <AskSchemaAgent label={selected} scope="proposal" />}
        </div>
      )}
    </div>
  )
}

function QuestionEvidence({ row }: { row: SchemaQuestionCoverage }) {
  const { t } = useTranslation()
  const Icon = row.answerable ? Check : Circle
  return (
    <div className="flex gap-3 py-2">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">
          {FIXED_QUESTIONS.has(row.question)
            ? t(`schemaAgent.questions.${row.question}`)
            : row.question}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t(row.answerable ? 'schemaAgent.review.answerable' : 'schemaAgent.review.unanswered')}
        </p>
        <ReviewDisclosure title={t('schemaAgent.review.details')}>
          {row.explanation && (
            <p className="text-sm leading-relaxed text-muted-foreground">{row.explanation}</p>
          )}
          <ul className="mt-3 space-y-2 break-words text-xs text-muted-foreground">
            {row.patterns.map((pattern, index) => (
              <li key={`${pattern.join(':')}:${index}`}>{pattern.join(' → ')}</li>
            ))}
            {row.properties.map(property => (
              <li key={property}>{property}</li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            {row.views.map(view => (
              <Badge key={view} variant="secondary">
                {t(`schemaAgent.review.views.${view}`)}
              </Badge>
            ))}
          </div>
        </ReviewDisclosure>
      </div>
    </div>
  )
}

export function ProposalEvidence({ proposal }: { proposal: SchemaProposal }) {
  const { t } = useTranslation()
  return (
    <div className="min-w-0">
      {Object.keys(proposal.typeReasons).length > 0 && (
        <ReviewDisclosure
          title={t('schemaAgent.review.typeReasons')}
          count={Object.keys(proposal.typeReasons).length}
        >
          <p className="mb-3 text-xs text-muted-foreground">{t('schemaAgent.review.selectType')}</p>
          <TypeEvidence proposal={proposal} />
        </ReviewDisclosure>
      )}
      {proposal.coverage.length > 0 && (
        <ReviewDisclosure title={t('schemaAgent.review.coverage')} count={proposal.coverage.length}>
          {proposal.coverage.map((row, index) => (
            <QuestionEvidence key={`${row.question}:${index}`} row={row} />
          ))}
        </ReviewDisclosure>
      )}
      {proposal.changes.length > 0 && (
        <ReviewDisclosure title={t('schemaAgent.review.changes')} count={proposal.changes.length}>
          {proposal.changes.map((change, index) => (
            <ReviewDisclosure
              key={`${change.target}:${index}`}
              title={`${t(`schemaAgent.review.operations.${change.op}`)} · ${change.target}`}
            >
              <p className="text-sm leading-relaxed text-muted-foreground">{change.reason}</p>
            </ReviewDisclosure>
          ))}
        </ReviewDisclosure>
      )}
    </div>
  )
}
